import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Overview from './components/Overview.jsx';
import TruckVolume from './components/TruckVolume.jsx';
import YardVisits from './components/YardVisits.jsx';
import Revenue from './components/Revenue.jsx';
import Billing from './components/Billing.jsx';
import Balances from './components/Balances.jsx';
import VendorSpend from './components/VendorSpend.jsx';
import Users from './components/Users.jsx';
import Review from './components/Review.jsx';
import Reference from './components/Reference.jsx';
import Usage from './components/Usage.jsx';
import AdminAttention from './components/AdminAttention.jsx';
import OutgoingAnalytics from './components/OutgoingAnalytics.jsx';
import OutgoingLoads from './components/OutgoingLoads.jsx';
import DailyReport   from './components/DailyReport.jsx';
import VehicleLog    from './components/VehicleLog.jsx';
import { useQboStatus, QboChip, QboBanner } from './components/QboStatus.jsx';

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFinance, setIsFinance] = useState(false);
  const [perms, setPerms] = useState({});
  const [accessStatus, setAccessStatus] = useState('checking');
  const [viewAsGroup, setViewAsGroup] = useState(() => {
    try { return localStorage.getItem('viewAsGroup') || ''; } catch { return ''; }
  });
  const [viewingAsGroup, setViewingAsGroup] = useState(null);
  const [groupsList, setGroupsList] = useState([]);

  // QuickBooks connection status: accounting + admin only (real role,
  // not the admin's view-as preview).
  const qbo = useQboStatus(!!user && accessStatus === 'allowed' && (isAdmin || isFinance));

  const location = useLocation();
  // Usage tracking — fire a page-view ping on every route change for any
  // signed-in user. Fire-and-forget; failures swallowed. The endpoint
  // requires allowlist auth, so a malicious unauthenticated user can't
  // pollute the table.
  useEffect(() => {
    if (!location || !location.pathname) return;
    fetch('/api/usage/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ event_type: 'page_view', page: location.pathname })
    }).catch(() => {});
  }, [location && location.pathname]);

  useEffect(() => {
    if (import.meta.env.VITE_LOCAL_DEV === 'true') {
      setUser({ userDetails: 'local-dev@example.com', userRoles: ['authenticated'] });
      setLoading(false);
      return;
    }
    fetch('/.auth/me')
      .then((r) => r.json())
      .then((data) => { setUser(data.clientPrincipal); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const url = viewAsGroup
      ? '/api/whoami?viewAs=' + encodeURIComponent(viewAsGroup)
      : '/api/whoami';
    fetch(url, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (!d) { setAccessStatus('denied'); return; }
        // isAdmin/isFinance always reflect the REAL user; only
        // effectivePermissions changes when view-as is active.
        setIsAdmin(!!d.isAdmin);
        setIsFinance(!!d.isFinance);
        setPerms(d.effectivePermissions || {});
        setViewingAsGroup(d.viewingAsGroup || null);
        setAccessStatus(d.isAllowed ? 'allowed' : 'denied');
      })
      .catch(() => setAccessStatus('denied'));
  }, [user, viewAsGroup]);

  // Load the group list for the view-as dropdown — only admins see it.
  useEffect(() => {
    if (!isAdmin) return;
    fetch('/api/auth/groups', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { groups: [] })
      .then((d) => setGroupsList(d.groups || []))
      .catch(() => {});
  }, [isAdmin]);

  function handleViewAsChange(g) {
    setViewAsGroup(g);
    try {
      if (g) localStorage.setItem('viewAsGroup', g);
      else   localStorage.removeItem('viewAsGroup');
    } catch {}
  }

  // Detect a return-from-signout via sessionStorage (more reliable than
  // chasing the post_logout_redirect_uri through SWA -> MS -> back, which
  // Microsoft often strips). handleSignOut sets the flag right before
  // navigating to /.auth/logout. We clear it the moment the user clicks
  // Sign-in again so a subsequent expiry doesn't strand them on the
  // signed-out page.
  const isSignedOutReturn = typeof window !== 'undefined'
    && (() => { try { return sessionStorage.getItem('justSignedOut') === '1'; } catch { return false; } })();
  function clearSignedOutFlag() {
    try { sessionStorage.removeItem('justSignedOut'); } catch {}
  }
  function handleSignOut(e) {
    if (e && e.preventDefault) e.preventDefault();
    try { sessionStorage.setItem('justSignedOut', '1'); } catch {}
    // Chain SWA logout -> MS common logout -> back to our origin so the
    // Microsoft session itself is invalidated. Without the MS step, only
    // the SWA cookie is cleared and the next sign-in silently re-uses
    // the same account because MS still has an active session.
    const back = window.location.origin + '/';
    const msLogout = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=' + encodeURIComponent(back);
    window.location.href = '/.auth/logout?post_logout_redirect_uri=' + encodeURIComponent(msLogout);
  }

  // SIGN-IN URL — used by both the auto-redirect (first visit) and the
  // explicit Sign-in button on the signed-out page. prompt=login forces
  // re-authentication; the signed-out variant also adds login_hint='' so
  // MS doesn't silently reuse the just-logged-out account.
  const SIGNIN_URL = '/.auth/login/aad?post_login_redirect_uri=/&prompt=select_account';
  const SIGNIN_URL_FORCED = '/.auth/login/aad?post_login_redirect_uri=/&prompt=select_account';

  if (loading) return <div className="loading">Loading…</div>;
  if (!user) {
    if (isSignedOutReturn) {
      return (
        <div className="app">
          <header className="topbar">
            <div className="topbar-brand"><img src="/ecovac-logo.png" alt="EcoVac" /></div>
          </header>
          <main className="main-full" style={{ maxWidth: 520, margin: '5rem auto', textAlign: 'center' }}>
            <h2>You've been signed out</h2>
            <p className="note">Click below to sign back in. You'll be asked to pick which Microsoft account to use.</p>
            <a href={SIGNIN_URL_FORCED} className="refresh-btn"
               onClick={clearSignedOutFlag}
               style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none', padding: '0.6rem 1.2rem' }}>
              Sign in
            </a>
          </main>
        </div>
      );
    }
    // First visit (or session expired without explicit sign-out) — auto-redirect.
    window.location.href = SIGNIN_URL;
    return <div className="loading">Redirecting to sign-in…</div>;
  }


  // Tab visibility: explicit per-tab permission wins. If perms is empty
  // (user not yet migrated to groups, or schema-003 not applied), fall back
  // to the legacy isAdmin/isFinance heuristic so nobody gets locked out.
  // When view-as is active, an admin should see EXACTLY what the viewed
  // group sees — so we skip the isAdmin auto-pass.
  const hasGroupPerms = Object.keys(perms).length > 0;
  function canSee(tab) {
    if (isAdmin && !viewingAsGroup) return true;
    if (hasGroupPerms) {
      const level = perms[tab];
      return !!level && level !== 'none';
    }
    // Legacy fallback (only used when groups never applied)
    if (tab === 'users' || tab === 'reference') return isAdmin;
    if (['revenue','vendor-spend','review'].includes(tab)) return isFinance;
    if (tab === 'attention') return isFinance;          // was role-gated
    if (tab === 'truck-analytics') return isAdmin;      // was admin-only
    return true;
  }

  // True when the user has at least 'write' on this tab. Used to gate
  // mutating buttons (Generate statements, Close cycle, +add, etc.) so
  // read-only viewers see a clean, non-editable view.
  function canWrite(tab) {
    if (isAdmin && !viewingAsGroup) return true;
    if (hasGroupPerms) {
      const level = perms[tab];
      return level === 'write' || level === 'admin';
    }
    // Legacy fallback: assume write if user can see the tab and isFinance or isAdmin
    if (tab === 'users' || tab === 'reference') return isAdmin;
    if (['revenue','vendor-spend','review','balances','billing'].includes(tab)) return isFinance;
    return isAdmin;
  }

  if (accessStatus === 'checking') return <div className="loading">Checking access…</div>;

  if (accessStatus === 'denied') {
    return (
      <div className="app">
        <header className="topbar">
          <div className="topbar-brand"><img src="/ecovac-logo.png" alt="EcoVac" /></div>
          <div style={{ flex: 1 }}></div>
          <div className="topbar-user">
            <QboChip qbo={qbo} />
          <span className="small">{user.userDetails}</span>
            <a href="#" onClick={handleSignOut} className="small">Sign out</a>
          </div>
        </header>
        <main className="main-full" style={{ maxWidth: 600, margin: '4rem auto', textAlign: 'center' }}>
          <h2 style={{ color: 'var(--down)' }}>No access</h2>
          <p>You're signed in as <strong>{user.userDetails}</strong>, but this account isn't on the EcoVac Dashboard allowlist.</p>
          <p className="note">Contact your admin to be added, then sign out and sign back in.</p>
          <a href="#" onClick={handleSignOut} className="refresh-btn" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none' }}>Sign out</a>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-brand">
          <img src="/ecovac-logo.png" alt="EcoVac" />
        </div>
        <nav className="topbar-nav">
          {canSee('overview')     && <NavLink to="/overview">Overview</NavLink>}
          {canSee('trucks')       && <NavLink to="/trucks">Truck Volume</NavLink>}
          {canSee('trucks')       && <NavLink to="/yard-visits">Yard visits</NavLink>}
          {canSee('billing')      && <NavLink to="/billing">Revenue Recognition</NavLink>}
          {canSee('balances')     && <NavLink to="/balances">Balances</NavLink>}
          {canSee('revenue')      && <NavLink to="/revenue">Billing (QBO)</NavLink>}
          {canSee('vendor-spend') && <NavLink to="/vendor-spend">Vendor Spend</NavLink>}
          {canSee('review')       && <NavLink to="/review">Review</NavLink>}
          {canSee('reference')    && <NavLink to="/reference">Reference</NavLink>}
          {canSee('outgoing-loads') && <NavLink to="/outgoing-loads">Outgoing loads</NavLink>}
          {canSee('daily-report')   && <NavLink to="/daily-report">Daily report</NavLink>}
          {canSee('vehicle-log')    && <NavLink to="/vehicle-log">Vehicle Log</NavLink>}
          {canSee('users')        && <NavLink to="/users">Users</NavLink>}
          {(canSee('attention') || ((isAdmin || isFinance) && !viewingAsGroup)) && <NavLink to="/attention">🔔 Attention</NavLink>}
          {(canSee('truck-analytics') || (isAdmin && !viewingAsGroup)) && <NavLink to="/outgoing-analytics">Truck analytics</NavLink>}
          {(canSee('truck-analytics') || (isAdmin && !viewingAsGroup)) && <NavLink to="/buy-vs-outsource">Buy vs outsource</NavLink>}
          {isAdmin && !viewingAsGroup && <NavLink to="/usage">Usage</NavLink>}
        </nav>
        <div className="topbar-user">
          {isAdmin && (
            <select value={viewAsGroup} onChange={(e) => handleViewAsChange(e.target.value)}
              title="Preview the dashboard as another group (admin only)"
              style={{ marginRight: '0.5rem', padding: '0.25rem 0.4rem', fontSize: '0.8em',
                       borderRadius: 4, border: viewingAsGroup ? '1px solid #b91c1c' : '1px solid #cbd5e1',
                       background: viewingAsGroup ? '#fef2f2' : '#fff' }}>
              <option value="">View as: (my real perms)</option>
              {groupsList.map(g => (
                <option key={g.id} value={g.name}>View as: {g.name}</option>
              ))}
            </select>
          )}
          <span className="small">{user.userDetails}</span>
          <a href="#" onClick={handleSignOut} className="small">Sign out</a>
        </div>
      </header>
      <QboBanner qbo={qbo} />
      {viewingAsGroup && (
        <div style={{ background: '#fef2f2', color: '#991b1b', padding: '0.5rem 1rem',
                      borderBottom: '1px solid #fecaca', display: 'flex',
                      justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
          <span>👀 Viewing as <strong>{viewingAsGroup}</strong> — UI is gated to this group's permissions. Your real admin access is still in effect for any server actions you trigger.</span>
          <button onClick={() => handleViewAsChange('')}
            style={{ background: '#991b1b', color: '#fff', border: 'none',
                     padding: '0.2rem 0.6rem', borderRadius: 4, cursor: 'pointer', fontSize: '0.85em' }}>
            Exit view-as
          </button>
        </div>
      )}
      <main className="main-full">
        <Routes>
          <Route path="/" element={<Navigate to="/trucks" replace />} />
          {canSee('overview')     && <Route path="/overview" element={<Overview />} />}
          {canSee('trucks')       && <Route path="/trucks" element={<TruckVolume />} />}
          {canSee('trucks')       && <Route path="/yard-visits" element={<YardVisits />} />}
          {canSee('trucks')       && <Route path="/camera-images" element={<Navigate to="/yard-visits" replace />} />}
          {canSee('billing')      && <Route path="/billing" element={<Billing />} />}
          {canSee('balances')     && <Route path="/balances" element={<Balances canWrite={canWrite('balances')} />} />}
          {canSee('revenue')      && <Route path="/revenue" element={<Revenue />} />}
          {canSee('vendor-spend') && <Route path="/vendor-spend" element={<VendorSpend />} />}
          {canSee('review')       && <Route path="/review" element={<Review canWrite={canWrite('review')} />} />}
          {canSee('reference')    && <Route path="/reference" element={<Reference canWrite={canWrite('reference')} />} />}
          {canSee('outgoing-loads') && <Route path="/outgoing-loads" element={<OutgoingLoads canWrite={canWrite('outgoing-loads')} />} />}
          {canSee('daily-report')   && <Route path="/daily-report" element={<DailyReport />} />}
          {canSee('vehicle-log')    && <Route path="/vehicle-log" element={<VehicleLog canWrite={canWrite('vehicle-log')} />} />}
          {canSee('users')        && <Route path="/users" element={<Users />} />}
          {(canSee('attention') || ((isAdmin || isFinance) && !viewingAsGroup)) && <Route path="/attention" element={<AdminAttention />} />}
          {(canSee('truck-analytics') || (isAdmin && !viewingAsGroup)) && <Route path="/outgoing-analytics" element={<OutgoingAnalytics view="analysis" />} />}
          {(canSee('truck-analytics') || (isAdmin && !viewingAsGroup)) && <Route path="/buy-vs-outsource" element={<OutgoingAnalytics view="buy" />} />}
          {isAdmin && !viewingAsGroup && <Route path="/usage" element={<Usage />} />}
        </Routes>
      </main>
    </div>
  );
}
